import { useSKUStore } from "@/stores/skuStore";
import { updateSkuSchema } from "@/schemas/sku/create";
import { useSkuFormFields } from "@/hooks/sku/useSkuFormFields";
import { useDetailPage } from "@/hooks/useDetailPage";
import { Button } from "@ab-inbev-maz/compass";
import NavigationHeader from "@/components/navigation/NavigationHeader";
import Loading from "@/features/sso-auth/components/Loading";
import { FormFieldsStep, type FormFieldsStepRef } from "@/components/commos/FormStep";
import { DetailView } from "@/components/commos/DetailView";
import ImagePreview from "@/components/commos/ImagePreview";
import DetailPageFileUpload from "@/components/commos/DetailPageFileUpload";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { catalogService } from "@/services/catalogService";


const details = [
  {
    id: 'sku_number',
    label: "ID de SKU",
  },
  {
    id: 'name',
    label: "Nombre",
  },
  {
    id: 'brand',
    label: "Marca",
  },
  {
    id: 'presentation',
    label: "Presentación",
  },
  {
    id: 'volume',
    label: "Volumen",
  },
  {
    id: 'quantity',
    label: "Cantidad",
  },
  {
    id: 'tipology',
    label: "Tipología de producto",
  },
  {
    id: 'size',
    label: "Tamaño",
  },
  {
    id: 'created_at',
    label: "Fecha de creación",
  }
];

const DetailSku = () => {
  const { id } = useParams();
  const getSKU = useSKUStore(state => state.getSKU);
  const updateSKU = useSKUStore(state => state.updateSKU);
  const deleteSKU = useSKUStore(state => state.deleteSKU);

  const { fields, setCatalogs } = useSkuFormFields();
  const { handleDelete, onSubmit, isFetching, refImage } = useDetailPage({
    updateItem: updateSKU,
    deleteItem: deleteSKU,
    itemName: 'SKU',
    tablePath: '/skus/table',
  });

  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const formRef = useRef<FormFieldsStepRef>(null);

  const [values, setValues] = useState<Record<string, any>>({
    sku_number: '',
    name: '',
    brand: '',
    presentation: '',
    volume: '',
    quantity: '',
    tipology: '',
    created_at: '',
    size: ''
  });

  const valueDetails = useMemo(() => {
    return details.map((detail) => ({
      ...detail,
      value: values[detail.id] || '',
    }));
  }, [values]);

  useEffect(() => {
    const fetchSKU = async () => {
      if (!id) return;

      const { data } = await getSKU(id);
      if (!data) return;

      Object.entries({
        ...data,
        created_at: DateTime.fromISO(data.created_at).toFormat('dd/MM/yyyy'),
      }).forEach(([key, value]) => {
        setValues((prev) => ({ ...prev, [key]: value || '' }));
      });

      setSourceImage(data.image);
      setIsLoading(false);
    };
    void fetchSKU();
  }, [id]);

  useEffect(() => {
    const fetchCatalogs = async () => {
      const catalogs = await catalogService.getCatalogs();
      setCatalogs(catalogs);
    };
    void fetchCatalogs();
  }, [setCatalogs]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
          <Loading message="Esto solo va tomar un momento, estamos cargando el SKU." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <NavigationHeader
        title={`SKU: ${values.name || ''}`}
        description="Aquí podrás revisar toda la información de tu SKU"
        actions={[
          <Button
            key="delete-cancel"
            label={isEditing ? "Cancelar" : "Eliminar"}
            variant="secondary"
            rightIcon={isEditing ? "validation/close" : "system/trash"}
            onClick={isEditing ? setIsEditing.bind(null, false) : handleDelete}
            disabled={isFetching}
          />,
          <Button
            key="edit-save"
            label={isEditing ? "Guardar" : "Editar"}
            variant="primary"
            rightIcon={isEditing ? "functionalities/save" : "functionalities/edit-2"}
            onClick={isEditing ? () => formRef.current?.submit() : setIsEditing.bind(null, true)}
            disabled={isFetching}
          />,
        ]}
      />

      <hr className="border-gray-200 container mx-auto mt-2" />

      <div className="container mx-auto px-4 py-8">
        <p className="mb-3 text-sm font-medium text-gray-700">Información del SKU</p>
        <div className="grid lg:grid-cols-[1fr_375px] gap-4">
          <div>
            {
              isEditing
                ? (
                  <div>
                    <div className="p-6 rounded-xl border border-gray-200 bg-white">
                      <FormFieldsStep
                        ref={formRef}
                        schema={updateSkuSchema}
                        defaultValues={values}
                        fields={fields as any}
                        onComplete={onSubmit}
                        isFetching={isFetching}
                        formId="sku-general-info"
                        description=""
                        editMode={true}
                      />
                    </div>
                    <DetailPageFileUpload itemType="SKU" refImage={refImage} />
                  </div>
                ) : (
                  <DetailView
                    data={valueDetails}
                  />
                )
            }
          </div>

          <ImagePreview
            sourceImage={sourceImage}
            alt="SKU preview"
          />
        </div>
      </div>
    </div>
  );
};

export default DetailSku;